import { HANDWRITTEN_NOTE_CLOSING_INDENT_SPACES } from '~~/layers/base/constants/handwrittenNoteCompose'

/**
 * Builds the single Fabric/print string from note body plus optional closing and printed name.
 *
 * Optional fields that are missing or whitespace-only are omitted. When at least one optional
 * is present, a blank visual line (`\n\n`) is inserted after `message`, then each included
 * line is prepended with {@link HANDWRITTEN_NOTE_CLOSING_INDENT_SPACES} spaces. Both optionals
 * are separated by a single newline. When neither optional is present, `message` is returned
 * unchanged.
 *
 * @param message Required body text (may be empty).
 * @param closing Optional sign-off line.
 * @param printedName Optional printed name line.
 * @returns Combined string for preview, fit-check, persistence, and print export.
 */
export function composeHandwrittenNoteMessage(
    message: string,
    closing?: string | null,
    printedName?: string | null
): string {
    const indent = ' '.repeat(HANDWRITTEN_NOTE_CLOSING_INDENT_SPACES)
    const optionalLines: string[] = []
    const closingText = closing?.trim() ?? ''
    const printedNameText = printedName?.trim() ?? ''
    if (closingText) optionalLines.push(`${indent}${closingText}`)
    if (printedNameText) optionalLines.push(`${indent}${printedNameText}`)
    if (optionalLines.length === 0) return message
    return `${message}\n\n${optionalLines.join('\n')}`
}

/**
 * Prefers a stored composed string; otherwise builds one from the part fields.
 *
 * Legacy rows may have `composed_message` null while `message` / `closing` / `printed_name`
 * are populated — callers (admin export) use this so print output still concatenates.
 *
 * @param composedMessage Persisted concatenation, or null/undefined when unset.
 * @param message Body text used for the fallback compose.
 * @param closing Optional sign-off used for the fallback compose.
 * @param printedName Optional printed name used for the fallback compose.
 * @returns Stored composed string, or a freshly composed fallback.
 */
export function resolveComposedHandwrittenNoteMessage(
    composedMessage: string | null | undefined,
    message: string,
    closing?: string | null,
    printedName?: string | null
): string {
    if (composedMessage != null) return composedMessage
    return composeHandwrittenNoteMessage(message, closing, printedName)
}
